import {useState, useCallback} from 'react';
import type {EditorState} from 'draft-js';
import {commitTicketingMutation, RelayEnvironment} from '../../../graphql/forecastRelay';
import ReplySubmitMutation from '../../../../../mutations/ticketing/tickets/replySubmitMutation';
import StageAttachmentMutation from '../../../../../mutations/ticketing/tickets/stageAttachmentMutation';
import type {SendReplyInput as RelaySendReplyInput} from '../../../../../mutations/ticketing/tickets/__generated__/replySubmitMutation.graphql';
import {editorToHtml} from '../utils/editorToHtml';
import {extractInlineImages, type ExtractedImage} from '../utils/extractInlineImages';

export interface AttachmentSubmitRef {
	storageKey: string;
	filename: string;
	contentType: string;
	sizeBytes: number;
	inlineContentId?: string;
}

export interface ReplySubmitInput {
	ticketId: string;
	editorState: EditorState;
	to: string[];
	cc?: string[];
	bcc?: string[];
	attachments?: AttachmentSubmitRef[];
}

export async function uploadInlineImage(_ticketId: string, image: ExtractedImage): Promise<AttachmentSubmitRef> {
	const buffer = image.data.buffer.slice(image.data.byteOffset, image.data.byteOffset + image.data.byteLength) as ArrayBuffer;
	const blob = new Blob([buffer], {type: image.mimeType});
	const file = new File([blob], image.filename, {type: image.mimeType});

	const staged = await new Promise<{
		storageKey: string;
		filename: string;
		contentType: string;
		sizeBytes: number;
	}>((resolve, reject) => {
		StageAttachmentMutation.commit(
			RelayEnvironment.getInstance(),
			response => {
				const s = response.stageAttachment;
				if (!s) {
					reject(new Error('Inline image upload failed'));
					return;
				}
				resolve(s);
			},
			err => reject(err),
			{file}
		);
	});

	return {
		storageKey: staged.storageKey,
		filename: staged.filename,
		contentType: staged.contentType,
		sizeBytes: staged.sizeBytes,
		inlineContentId: image.contentId,
	};
}

interface UseReplySubmitResult {
	submit: (input: ReplySubmitInput) => Promise<unknown>;
	loading: boolean;
	error: string | null;
}

export function useReplySubmit(): UseReplySubmitResult {
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const submit = useCallback(async (input: ReplySubmitInput) => {
		setError(null);
		setLoading(true);
		const contentState = input.editorState.getCurrentContent();
		const rawHtml = editorToHtml(contentState);
		const {html: bodyHtml, images} = extractInlineImages(rawHtml);
		const bodyText = contentState.getPlainText('\n');

		let inlineAttachmentRefs: AttachmentSubmitRef[] = [];
		if (images.length > 0) {
			inlineAttachmentRefs = await Promise.all(images.map(img => uploadInlineImage(input.ticketId, img)));
		}

		const allAttachments = [...(input.attachments ?? []), ...inlineAttachmentRefs];

		const mutationInput: RelaySendReplyInput = {
			ticketId: input.ticketId,
			bodyHtml,
			bodyText,
			to: input.to,
			cc: input.cc?.length ? input.cc : undefined,
			bcc: input.bcc?.length ? input.bcc : undefined,
			attachments: allAttachments.length > 0 ? allAttachments : undefined,
		};

		return new Promise((resolve, reject) => {
			commitTicketingMutation(
				ReplySubmitMutation,
				mutationInput,
				response => {
					setLoading(false);
					resolve(response.sendReply.message);
				},
				err => {
					setLoading(false);
					setError(err instanceof Error ? err.message : String(err));
					reject(err);
				}
			);
		});
	}, []);

	return {submit, loading, error};
}
